import React, { forwardRef, useId } from "react";
import { cx } from "components/fields/FormField";

/**
 * On/off toggle rendered as `<button role="switch" aria-checked>`.
 *
 * Props:
 * - checked=false, disabled=false.
 * - onChange(event): called with `{ target: { checked, name, id } }` holding the NEXT state,
 *   so handlers written for a checkbox (`e.target.checked`) keep working.
 * - label: inline label node (click toggles); description: helper text (aria-describedby).
 * - id: auto via useId when omitted; name: echoed in the change event.
 * - className: classes for the outer element (the button itself when there is no label/description).
 * - aria-label etc.: forwarded to the button (give an aria-label when there is no visible label).
 * - ref: forwarded to the button.
 */
const Switch = forwardRef(function Switch(
  {
    id,
    name,
    checked = false,
    onChange,
    label,
    description,
    disabled = false,
    className = "",
    ...rest
  },
  ref
) {
  const autoId = useId();
  const switchId = id || autoId;
  const descriptionId = description ? `${switchId}-description` : undefined;
  const hasText = Boolean(label || description);

  const toggle = () => {
    if (disabled) return;
    onChange?.({ target: { checked: !checked, name, id: switchId } });
  };

  const control = (
    <button
      ref={ref}
      type="button"
      role="switch"
      id={switchId}
      aria-checked={checked}
      aria-describedby={descriptionId}
      disabled={disabled}
      onClick={toggle}
      {...rest}
      className={cx(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-200 focus-ring disabled:cursor-not-allowed disabled:opacity-60",
        checked ? "border-transparent bg-primary" : "border-line bg-surface-subtle",
        !hasText && className
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          "absolute left-0.5 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-white shadow transition-transform duration-200",
          checked ? "translate-x-[18px]" : "translate-x-0"
        )}
      />
    </button>
  );

  if (!hasText) return control;

  return (
    <div className={cx("flex items-start gap-3", className)}>
      {control}
      <div className="flex min-w-0 flex-col pt-0.5">
        {label && (
          <label
            htmlFor={switchId}
            className={cx(
              "text-sm font-medium text-fg",
              disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            )}
          >
            {label}
          </label>
        )}
        {description && (
          <p id={descriptionId} className="text-xs text-fg-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
});

export default Switch;
